import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge
} from "@mui/material";
import { FaEye, FaExchangeAlt, FaEdit, FaTimes, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const VendorModal = ({ isOpen, onClose, data, mode, onSwitchMode, onOpenReview, onApprove, onReject, onRequestEdit, isPending, source, hideApproveReject }) => {
  const [activeTab, setActiveTab] = useState(mode === 'changes' ? 1 : 0);
  console.log(hideApproveReject, isPending, "hide")

  useEffect(() => {
    setActiveTab(mode === 'changes' ? 1 : 0);
  }, [mode]);

  if (!data) return null;

  // Handle both API response formats
  const is_new_submission = data.is_new_submission ?? (data.parent_profile === null && data.parent_profile_id === null);
  const entry = data.entry || data;
  const diff = data.diff || [];
  const entry_status = data.entry?.status || data.status || 'Unknown';
  const changed_fields_count = data.changed_fields_count || diff.length || 0;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (onSwitchMode) {
      onSwitchMode(newValue === 1 ? 'changes' : 'details');
    }
  };

  // Format field name for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Check if value is a URL
  const isUrl = (value) => {
    if (typeof value !== 'string') return false;
    return value.match(/^https?:\/\/.+/i) !== null;
  };

  // Render clickable URL
  const renderClickableUrl = (url) => {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#ee448b', textDecoration: 'underline' }}
      >
        {url}
      </a>
    );
  };

  // Better format value for display
  const formatValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    // Handle operating_cities array of objects
    if (fieldName === 'operating_cities' && Array.isArray(value)) {
      if (value.length === 0) return '-';
      return value.map(city => city.name || city.city_name || city.label || JSON.stringify(city)).join(', ');
    }

    // Handle form_data object
    if (fieldName === 'form_data' && typeof value === 'object') {
      try {
        const entries = Object.entries(value);
        if (entries.length === 0) return '-';
        return entries.map(([k, v]) => {
          const displayValue = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
          return `${formatFieldName(k)}: ${displayValue}`;
        }).join(', ');
      } catch {
        return JSON.stringify(value);
      }
    }

    // Handle package_details object
    if (fieldName === 'package_details' && typeof value === 'object') {
      try {
        const entries = Object.entries(value);
        if (entries.length === 0) return '-';
        return entries.map(([k, v]) => `${formatFieldName(k)}: ${v || '-'}`).join(', ');
      } catch {
        return JSON.stringify(value);
      }
    }

    // Handle other arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Check if array of strings
      if (value.every(item => typeof item === 'string')) {
        return value.join(', ');
      }
      // Array of objects - try to extract names
      return value.map(item => {
        if (typeof item === 'object' && item !== null) {
          return item.name || item.label || item.value || JSON.stringify(item);
        }
        return String(item);
      }).join(', ');
    }

    // Handle objects
    if (typeof value === 'object') {
      try {
        const entries = Object.entries(value);
        if (entries.length === 0) return '-';
        return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
      } catch {
        return JSON.stringify(value);
      }
    }

    return String(value);
  };

  // Get status color
  const getStatusColor = (status) => {
    const statusColors = {
      'APPROVED': 'success',
      'PENDING': 'warning',
      'PENDING_APPROVAL': 'warning',
      'EDIT_REQUESTED': 'info',
      'REJECTED': 'error',
      'DRAFT': 'default',
      'MIGRATED': 'success'
    };
    return statusColors[status?.toUpperCase()] || 'default';
  };

  // Define field sections
  const getFieldSections = () => {
    if (!entry) return [];

    return [
      {
        title: 'Basic Information',
        fields: ['business_name', 'business_type', 'category', 'service_title', 'service_description']
      },
      {
        title: 'Contact Information',
        fields: ['contact_person', 'email', 'phone_number', 'website_url', 'instagram_url', 'facebook_url', 'youtube_url', 'gmb_url']
      },
      {
        title: 'Location',
        fields: ['primary_city', 'primary_area', 'primary_city_name', 'primary_area_name', 'operating_cities', 'address', 'pincode', 'service_radius', 'destination_wedding']
      },
      {
        title: 'Business Details',
        fields: ['established_year', 'pan_number', 'gstin', 'languages', 'response_time', 'availability']
      },
      {
        title: 'Banking Information',
        fields: ['account_holder', 'account_number', 'ifsc_code', 'bank_name']
      },
      {
        title: 'Pricing',
        fields: ['starting_price', 'pricing_model', 'package_details']
      },
      {
        title: 'Media',
        fields: ['cover_image', 'portfolio_images']
      },
      {
        title: 'Category Details',
        fields: ['form_data']
      },
      {
        title: 'Package Details',
        fields: ['package_details']
      },
      {
        title: 'Other Information',
        fields: ['source', 'admin_remarks', 'terms_accepted']
      }
    ];
  };

  // Render field value
  const renderFieldValue = (field, value) => {
    // Handle images
    if ((field === 'cover_image' || field === 'portfolio_images') && value) {
      if (Array.isArray(value) && value.length > 0) {
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {value.map((img, idx) => (
              img && (
                <img
                  key={idx}
                  src={img}
                  alt={`${field}-${idx}`}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                />
              )
            ))}
          </Box>
        );
      }
      if (typeof value === 'string' && value.startsWith('http')) {
        return (
          <img
            src={value}
            alt={field}
            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd', marginTop: 8 }}
          />
        );
      }
    }

    // Handle form_data with structured layout
    if (field === 'form_data' && value && typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) return <Typography variant="body2">-</Typography>;

      return (
        <Box sx={{ mt: 1 }}>
          {entries.map(([key, val], idx) => {
            const displayValue = typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val);
            const isLink = isUrl(displayValue);

            return (
              <Box key={key} sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    minWidth: '180px',
                    color: '#666'
                  }}
                >
                  {formatFieldName(key)}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {isLink ? renderClickableUrl(displayValue) : displayValue}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    // Handle package_details with structured layout
    if (field === 'package_details' && value && typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) return <Typography variant="body2">-</Typography>;

      return (
        <Box sx={{ mt: 1 }}>
          {entries.map(([key, val]) => {
            const displayValue = val || '-';
            const isLink = isUrl(displayValue);

            return (
              <Box key={key} sx={{ display: 'flex', mb: 1, alignItems: 'flex-start' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    minWidth: '120px',
                    color: '#666'
                  }}
                >
                  {formatFieldName(key)}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {isLink ? renderClickableUrl(displayValue) : displayValue}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    // Handle URL fields
    if (typeof value === 'string' && isUrl(value)) {
      return (
        <Typography variant="body2">
          {renderClickableUrl(value)}
        </Typography>
      );
    }

    // Get formatted value
    const formattedValue = formatValue(value, field);

    // For long text, wrap it
    return (
      <Typography
        variant="body2"
        sx={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'pre-wrap'
        }}
      >
        {formattedValue}
      </Typography>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #eee' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {entry?.business_name || entry?.phone_number || 'Vendor Details'}
            </Typography>
            <Chip
              label={entry_status}
              color={getStatusColor(entry_status)}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {is_new_submission && (
              <Badge color="primary" badgeContent="NEW" sx={{ ml: 1 }} />
            )}
          </Box>
          <Button onClick={onClose} size="small" sx={{ minWidth: 'auto', p: 1, color: 'text.secondary' }}>
            <FaTimes size={20} />
          </Button>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: '#f5f5f5' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="vendor tabs">
          <Tab
            icon={<FaEye size={14} />}
            iconPosition="start"
            label="Details"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<FaExchangeAlt size={14} />}
            iconPosition="start"
            label={`Changes ${changed_fields_count > 0 ? `(${changed_fields_count})` : ''}`}
            disabled={is_new_submission || !diff || diff.length === 0}
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {activeTab === 0 && (
          // Details View
          <Box>
            {is_new_submission && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(238, 68, 139, 0.1)', borderRadius: 1, border: '1px solid #ee448b' }}>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                  This is a new vendor submission. No previous version exists.
                </Typography>
              </Box>
            )}

            {getFieldSections().map((section, idx) => {
              // Check if section has any non-empty fields
              const hasData = section.fields.some(field => {
                const value = entry?.[field];
                return value !== null && value !== undefined && value !== '' &&
                  !(Array.isArray(value) && value.length === 0) &&
                  !(typeof value === 'object' && Object.keys(value).length === 0);
              });

              if (!hasData) return null;

              return (
                <Box key={section.title} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ee448b', mb: 1.5, fontSize: '1rem' }}>
                    {section.title}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense sx={{ py: 0 }}>
                    {section.fields.map(field => {
                      const value = entry?.[field];
                      // Skip empty values
                      if (value === null || value === undefined || value === '' ||
                        (Array.isArray(value) && value.length === 0) ||
                        (typeof value === 'object' && Object.keys(value).length === 0)) {
                        return null;
                      }
                      return (
                        <ListItem key={field} sx={{ py: 0.5, px: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                {formatFieldName(field)}
                              </Typography>
                            }
                            secondary={renderFieldValue(field, value)}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}

        {activeTab === 1 && (
          // Changes View
          <Box>
            {diff && diff.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(238, 68, 139, 0.1)' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#f44336', width: '37.5%' }}>Current Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#4caf50', width: '37.5%' }}>Pending Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diff.map((change, index) => (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                          {formatFieldName(change.field)}
                        </TableCell>
                        <TableCell sx={{ color: '#666', verticalAlign: 'top', wordBreak: 'break-word' }}>
                          {formatValue(change.current_value, change.field)}
                        </TableCell>
                        <TableCell sx={{ color: '#ee448b', fontWeight: 500, verticalAlign: 'top', wordBreak: 'break-word' }}>
                          {formatValue(change.pending_value, change.field)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No changes detected
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>

        {/* Approve/Reject/Request Edit for PENDING vendors */}
        {isPending && !hideApproveReject && (
          <>
            <Button
              onClick={onRequestEdit}
              variant="contained"
              size="small"
              startIcon={<FaEdit />}
              disabled={!onRequestEdit}
              sx={{ bgcolor: '#2196f3', '&:hover': { bgcolor: '#1976d2' }, textTransform: 'none' }}
            >
              Request Edit
            </Button>
            <Button
              onClick={onReject}
              variant="contained"
              size="small"
              startIcon={<FaTimesCircle />}
              disabled={!onReject}
              sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, textTransform: 'none' }}
            >
              Reject
            </Button>
            <Button
              onClick={onApprove}
              variant="contained"
              size="small"
              startIcon={<FaCheckCircle />}
              disabled={!onApprove}
              sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' }, textTransform: 'none' }}
            >
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VendorModal;
